import React, { useEffect, useState } from "react";
import { Control, FormProvider, useForm, UseFormWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import first from "lodash/first";
import isEmpty from "lodash/isEmpty";

import { useCreateReservation } from "pages/new/hooks/useCreateReservation";
import { useCreateRequestContext } from "pages/new/context/useCreateRequestContext";
import CreateReservationFormFields from "./components/CreateReservationFormFields";
import { useAuth, useCurrentUser, useOperator } from "globals/hooks";
import {
  useAuthenticationChangeUpdate,
  useUnmountUpdate,
  useUpdateBookingContact,
} from "components/confirm/form/hooks";
import { useIsGratuityEnabled } from "components/confirm/form/hooks/useIsGratuityEnabled";
import { getCreateReservationFormDefaultValues } from "./form/defaultValues";
import {
  CreateReservationFormState,
  createReservationFormErrorSchema,
} from "./form/schemaValidation";
import { useConsentSignature } from "pages/new/hooks/useConsentSignature";
import ConsentSignatureDialog from "components/confirm/form/components/TermsAndConditionsBlock/components/ConsentSignatureDialog/ConsentSignatureDialog";
import { CreateTripState } from "pages/new/context/initialState";
import { useDisplayMeetGreet } from "components/confirm/form/hooks/useDisplayMeetGreet";
import { usePricelessBookingContext } from "../../context";
import { ConfirmReservationFormState } from "pages/order/components/ConfirmReservationForm/form/schemaValidation";
import { getIsNetflixLogin } from "utils/auth/isNetflixLogin";
import useTurnstileVerification from "components/security/useTurnstileVerification";
import { useReservationPaymentSetup } from "pages/new/hooks/useReservationPaymentSetup";
import { useReservationPaymentSetupStore } from "pages/new/context/ReservationPaymentSetupProvider";

type CreateReservationFormProps = {
  trips: CreateTripState[];
};

function CreateReservationFormSession(props: CreateReservationFormProps) {
  const { trips } = props;

  // contexts
  const [request] = useCreateRequestContext();
  const { isPricelessBooking } = usePricelessBookingContext();

  // hooks
  const {
    operator: {
      enableCreditCardWhenBooking,
      driverGratuityPresets,
      gratuityOptional,
      customGratuityWhenBooking,
      customGratuityNoMinimum,
      settings: { requireConsentWhenBooking, requireSignatureWhenBooking } = {},
    },
  } = useOperator();
  const currentUser = useCurrentUser();
  const paymentStore = useReservationPaymentSetupStore();
  const { authStage } = useAuth();
  const isGratuityEnabled = useIsGratuityEnabled({
    trips,
    mode: isPricelessBooking ? "priceless" : "create",
  });
  const consentSignature = useConsentSignature({ trips });
  const turnstile = useTurnstileVerification({
    action: "reservation_create",
  });

  const { displayMeetGreet } = useDisplayMeetGreet({
    isAirportPickUpStop:
      !!request.trip.stops[0].airport?.icaoCode ||
      !!request.returnTrip?.stops[0]?.airport?.icaoCode,
    mode: "create",
    createPickUpAddressIsAirport:
      request.trip.stops[0].pickUpGooglePlaceTypes?.includes("airport"),
  });

  // hooks continued
  const methods = useForm<CreateReservationFormState>({
    defaultValues: {
      ...getCreateReservationFormDefaultValues(
        request.bookingContact,
        request.trip
      ),
      ...paymentStore.state?.restoredForm,
    },
    mode: "onSubmit",
    resolver: yupResolver(createReservationFormErrorSchema),
    context: {
      isGratuityEnabled,
      gratuityOptional,
      customGratuityEnabled: customGratuityWhenBooking,
      customGratuityNoMinimum,
      minGratuityPct: first(driverGratuityPresets),
      isCreatingBookingContact: authStage !== "authenticated",
      isTermsRequired: requireConsentWhenBooking,
      isNewPaymentMethodRequired:
        enableCreditCardWhenBooking &&
        !paymentStore.state?.enabled &&
        isEmpty(currentUser?.paymentMethods),
      isPaymentElement: paymentStore.state?.enabled === true,
      isExistingPaymentMethodRequired:
        !paymentStore.state?.enabled &&
        enableCreditCardWhenBooking &&
        !!currentUser &&
        !(paymentStore.state?.resume && !paymentStore.state.result) &&
        (!isEmpty(currentUser.paymentMethods) || !!paymentStore.state?.result),
      isConsentSignatureRequired: requireSignatureWhenBooking,
      isPickUpVariantRequired: displayMeetGreet,
    },
  });

  useEffect(() => {
    if (paymentStore.state?.restoredForm) {
      paymentStore.update({ ...paymentStore.state, restoredForm: undefined });
    }
  }, [paymentStore]);

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    setError,
    formState: { errors: createReservationErrors },
  } = methods;

  const isNetflixLogin = getIsNetflixLogin();
  const reservationPaymentSetup = useReservationPaymentSetup(watch());

  // update global state
  useUnmountUpdate({ watch });
  useUpdateBookingContact({ watch, getValues });
  useAuthenticationChangeUpdate({ getValues, setValue, isNetflixLogin });

  const { createReservation, isCreatingReservation } = useCreateReservation({
    setError,
    createReservationFormState: getValues(),
    onVerificationConsumed: turnstile.resetVerification,
    reservationPaymentSetup,
  });

  // scroll to the sections with errors
  const handleInvalidForm = (errors) => {
    if (errors.pickUpVariantSelected) {
      document
        .getElementById("meet-and-greet")
        ?.scrollIntoView({ behavior: "smooth" });
    } else if (errors.driverGratuityPctCustomerInput) {
      document
        .getElementById("driver-gratuity")
        ?.scrollIntoView({ behavior: "smooth" });
    } else if (errors.bookingContact) {
      document
        .getElementById("booking-contact")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        id={FORM_ID}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();

          handleSubmit(() => {
            consentSignature.handleConsentSignatureDialogClose();
            if (!turnstile.token) {
              return;
            }

            createReservation(getValues(), turnstile.token);
          }, handleInvalidForm)(e);
        }}
      >
        <CreateReservationFormFields
          trips={trips}
          control={control}
          watch={watch}
          onConsentSignatureDialogOpen={
            consentSignature.handleConsentSignatureDialogOpen
          }
          isSubmitting={isCreatingReservation}
          formId={FORM_ID}
          setValue={setValue}
          createReservationErrors={createReservationErrors}
          getValues={getValues}
          disableSubmit={
            turnstile.disableSubmit ||
            (enableCreditCardWhenBooking &&
              reservationPaymentSetup.enabled &&
              (!!reservationPaymentSetup.state?.resume ||
                (!currentUser?.paymentMethods?.length &&
                  !reservationPaymentSetup.state?.result)) &&
              !reservationPaymentSetup.state?.addCardReturn &&
              !reservationPaymentSetup.setupState?.ready)
          }
          turnstileElement={turnstile.widget}
          reservationPaymentSetup={reservationPaymentSetup}
        />
        {/* Consent Signature Agreement */}
        {requireSignatureWhenBooking && (
          <ConsentSignatureDialog
            control={
              control as Control<
                CreateReservationFormState | ConfirmReservationFormState
              >
            }
            watch={
              watch as UseFormWatch<
                CreateReservationFormState | ConfirmReservationFormState
              >
            }
            setValue={setValue}
            formId={FORM_ID}
            isSubmitting={isCreatingReservation}
            isDialogOpen={consentSignature.consentSignatureDialogOpen}
            onClose={consentSignature.handleConsentSignatureDialogClose}
            consentSignature={consentSignature.consentSignature}
            userConsentAgreement={consentSignature.isAgreementChecked}
            onConsentSignatureChange={
              consentSignature.handleConsentSignatureChange
            }
            onUserConsentAgreementToggle={
              consentSignature.handleUserConsentAgreementToggle
            }
            selectedVehicleIds={consentSignature.selectedVehicleIds}
          />
        )}
      </form>
    </FormProvider>
  );
}

function CreateReservationForm(props: CreateReservationFormProps) {
  const { state } = useReservationPaymentSetupStore();
  const [initialized, setInitialized] = useState(false);
  // Validate the returned payer before hydrating, then preserve edits across login.
  useEffect(() => {
    if (state && !initialized) setInitialized(true);
  }, [state, initialized]);
  return initialized ? <CreateReservationFormSession {...props} /> : null;
}

export default CreateReservationForm;

// constants
const FORM_ID = "create-reservation-form";
